"use client";

import React, { useState, useEffect, useCallback, useContext } from "react";
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import dynamic from "next/dynamic";
import { PlusIcon, TrashIcon, Article, CheckIcon } from "@phosphor-icons/react";
import { Button } from "@tranquility-ai/theme-components/button";
import { useTimePilotT } from "@/i18n/useTimePilotT";
import {
  buildSharedDataLocalStorageKey,
  sharedDataCasesMatch,
  useOptionalToolsSharedCaseData,
} from "./tools-shared-case-data";
import { AuthContext } from "@/app/contexts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const MarkdownEditor = dynamic(
  () => import("@tranquility-ai/theme-components/markdown-editor").then((m) => m.MarkdownEditor),
  { ssr: false },
);

const RESEARCH_NOTES_STORAGE_PREFIX = "tpl-research-notes";

export function getResearchNotesStorageKey(
  caseId: string | undefined,
  tenantFriendlyName?: string | null,
): string {
  return buildSharedDataLocalStorageKey(
    RESEARCH_NOTES_STORAGE_PREFIX,
    caseId,
    tenantFriendlyName,
  );
}

export interface ResearchNote {
  id: string;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
}

function loadResearchNotes(storageKey: string): ResearchNote[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ResearchNote[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function defaultNote(): ResearchNote {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    title: "",
    content: "",
    createdAt: now,
    updatedAt: now,
  };
}

/** Hook for research notes state, keyed by caseId. */
export function useResearchNotesState(caseId: string | undefined) {
  const auth = useContext(AuthContext);
  const remote = useOptionalToolsSharedCaseData();
  const tenantFriendlyName = remote?.tenantFriendlyName ?? auth.tenantName ?? null;
  const storageKey = getResearchNotesStorageKey(caseId, tenantFriendlyName);
  const useRemoteNotes = Boolean(
    caseId &&
      remote &&
      sharedDataCasesMatch(caseId, remote.caseId, remote.tenantFriendlyName),
  );

  const [localNotes, setLocalNotes] = useState<ResearchNote[]>(() =>
    useRemoteNotes ? [] : loadResearchNotes(storageKey),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!useRemoteNotes) {
      setLocalNotes(loadResearchNotes(storageKey));
    }
    setSelectedId(null);
  }, [storageKey, useRemoteNotes]);

  const notes = useRemoteNotes
    ? remote!.hydrated
      ? (remote!.notes as ResearchNote[])
      : []
    : localNotes;
  const setNotes = useRemoteNotes ? remote!.setNotes : setLocalNotes;

  useEffect(() => {
    if (useRemoteNotes) return;
    const handler = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue != null) {
        try {
          const parsed = JSON.parse(event.newValue) as ResearchNote[];
          if (Array.isArray(parsed)) {
            setLocalNotes(parsed);
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey, useRemoteNotes]);

  const persist = useCallback(
    (next: ResearchNote[]) => {
      if (useRemoteNotes) {
        return;
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [useRemoteNotes, storageKey],
  );

  const addNote = useCallback((): string => {
    const note = defaultNote();
    setNotes((prev) => {
      const next = [note, ...prev];
      persist(next);
      return next;
    });
    setSelectedId(note.id);
    return note.id;
  }, [persist, setNotes]);

  const updateNote = useCallback(
    (id: string, updates: { title?: string; content?: string }) => {
      const now = Date.now();
      setNotes((prev) => {
        const idx = prev.findIndex((n) => n.id === id);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          ...(updates.title !== undefined && { title: updates.title }),
          ...(updates.content !== undefined && { content: updates.content }),
          updatedAt: now,
        };
        persist(next);
        return next;
      });
    },
    [persist, setNotes],
  );

  const deleteNote = useCallback(
    (id: string) => {
      setNotes((prev) => {
        const next = prev.filter((n) => n.id !== id);
        persist(next);
        return next;
      });
      setSelectedId((prev) => (prev === id ? null : prev));
    },
    [persist, setNotes],
  );

  const remoteRefetch = remote?.refetch;
  const refetch = useCallback(() => {
    if (useRemoteNotes && remoteRefetch) {
      void remoteRefetch();
      return;
    }
    setLocalNotes(loadResearchNotes(storageKey));
  }, [useRemoteNotes, remoteRefetch, storageKey]);

  const selectedNote = notes.find((n) => n.id === selectedId) ?? notes[0];

  return {
    notes,
    selectedId,
    setSelectedId,
    selectedNote,
    addNote,
    updateNote,
    deleteNote,
    refetch,
  };
}

const TITLE_MAX_LENGTH = 60;

function noteListTitle(note: ResearchNote, untitled: string): string {
  const title = (note.title || "").trim();
  if (title)
    return title.length <= TITLE_MAX_LENGTH
      ? title
      : title.slice(0, TITLE_MAX_LENGTH) + "…";
  return untitled;
}

function EditorLoading() {
  const t = useTimePilotT();
  return <div className="text-white/60 text-sm">{t("researchNotes.loadingEditor")}</div>;
}

export interface ResearchNotesContentProps {
  notes: ResearchNote[];
  selectedId: string | null;
  selectedNote: ResearchNote | null;
  onSelectNote: (id: string) => void;
  onAddNote: () => void;
  onUpdateNote: (
    id: string,
    updates: { title?: string; content?: string },
  ) => void;
  onDeleteNote: (id: string) => void;
  /** When the event modal is open for editing, applies note body to the Description field. */
  applyTextToEventDescription?: (text: string) => boolean;
  eventDescriptionApplyReady?: boolean;
}

/** Research Notes view: list of notes + WYSIWYG editor for the selected note. */
export function ResearchNotesContent({
  notes,
  selectedId,
  selectedNote,
  onSelectNote,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  applyTextToEventDescription,
  eventDescriptionApplyReady = false,
}: ResearchNotesContentProps) {
  const t = useTimePilotT();
  const [appliedToDescriptionNoteId, setAppliedToDescriptionNoteId] = useState<
    string | null
  >(null);

  const handleApplyNoteToEventDescription = useCallback(
    (note: ResearchNote) => {
      if (!applyTextToEventDescription || !eventDescriptionApplyReady) return;
      const text = note.content ?? "";
      const ok = applyTextToEventDescription(text);
      if (!ok) return;
      setAppliedToDescriptionNoteId(note.id);
      setTimeout(() => setAppliedToDescriptionNoteId(null), 1500);
    },
    [applyTextToEventDescription, eventDescriptionApplyReady],
  );

  return (
    <div className="flex flex-1 min-h-0 flex-row overflow-hidden">
      <div className="flex w-[240px] shrink-0 flex-col border-r border-white/20 overflow-hidden">
        <div className="shrink-0 px-3 py-2 border-b border-white/20 flex flex-row items-center justify-between gap-2">
          <span className="text-white/80 font-light text-[13px] tracking-wider">
            {t("researchNotes.title")}
          </span>
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color="light"
            background="inverse"
            onClick={onAddNote}
            aria-label={t("researchNotes.newNote")}
          >
            <PlusIcon weight="bold" />
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {notes.length === 0 ? (
            <p className="px-3 py-4 text-white/50 text-xs">
              {t("researchNotes.empty")}
            </p>
          ) : (
            <ul className="flex flex-col gap-0 list-none pl-0">
              {notes.map((note) => (
                <li
                  key={note.id}
                  className={`flex items-start gap-2 border-b border-white/10 px-3 py-2 cursor-pointer group ${
                    selectedNote?.id === note.id ? "bg-white/10" : "hover:bg-white/5"
                  }`}
                  onClick={() => onSelectNote(note.id)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-white/90 text-xs line-clamp-2 break-words">
                      {noteListTitle(note, t("researchNotes.untitled"))}
                    </p>
                    <p className="text-white/50 text-[11px] mt-1">
                      {dayjs(note.updatedAt).fromNow()}
                    </p>
                  </div>
                  <TpTooltip
                    content={
                      eventDescriptionApplyReady
                      ? t("researchNotes.applyReady")
                      : t("researchNotes.applyNeedsEvent")
                    }
                    placement="left"
                    delayDuration={0}
                  >
                    <Button
                      type="button"
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApplyNoteToEventDescription(note);
                      }}
                      disabled={!eventDescriptionApplyReady}
                      className="shrink-0 opacity-0 group-hover:opacity-100"
                      aria-label={t("researchNotes.apply")}
                    >
                      {appliedToDescriptionNoteId === note.id ? (
                        <CheckIcon weight="bold" className="text-green-500" />
                      ) : (
                        <Article size={14} weight="duotone" />
                      )}
                    </Button>
                  </TpTooltip>
                  <Button
                    type="button"
                    iconOnly
                    size="sm"
                    style="text"
                    color="danger"
                    background="inverse"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteNote(note.id);
                    }}
                    className="shrink-0 opacity-0 group-hover:opacity-100"
                    aria-label={t("researchNotes.delete")}
                  >
                    <TrashIcon weight="duotone" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="flex flex-1 min-w-0 flex-col p-3 overflow-hidden">
        {selectedNote ? (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <React.Suspense
              fallback={
                <EditorLoading />
              }
            >
              <MarkdownEditor
                key={selectedNote.id}
                id={`research-note-${selectedNote.id}`}
                title={selectedNote.title}
                titleAriaLabel={t("researchNotes.noteTitle")}
                titlePlaceholder={t("researchNotes.noteTitle")}
                onTitleChange={(title) =>
                  onUpdateNote(selectedNote.id, { title })
                }
                onTitleBlur={(title) => {
                  if (!title.trim())
                    onUpdateNote(selectedNote.id, { title: t("researchNotes.untitled") });
                }}
                markdown={selectedNote.content}
                onChange={(content) =>
                  onUpdateNote(selectedNote.id, { content })
                }
                debounceMs={300}
              />
            </React.Suspense>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center text-white/50 text-sm">
            {t("researchNotes.select")}
          </div>
        )}
      </div>
    </div>
  );
}
